/**
 * React Hook for Voice Chat
 *
 * Provides a React hook interface for voice chat functionality.
 * Uses REST API to fetch voice chat credentials instead of WebSocket.
 * Manages service lifecycle and state updates.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import { VoiceChatService } from '../services/voice-chat/voice-chat-service';
import { VoiceChatConfig, VoiceChatState } from '../types/voice-chat';
import { useVoiceChatCredentials } from './useVoiceChatCredentials';

export interface UseVoiceChatOptions {
  config: VoiceChatConfig;
  autoConnect?: boolean;
}

export interface UseVoiceChatReturn extends VoiceChatState {
  setMicrophoneEnabled: (enabled: boolean) => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  reconnect: () => Promise<void>;
}

export function useVoiceChat(options: UseVoiceChatOptions): UseVoiceChatReturn {
  const { config, autoConnect = true } = options;
  const serviceRef = useRef<VoiceChatService | null>(null);
  const [state, setState] = useState<VoiceChatState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    participants: [],
    activeSpeakers: [],
    microphoneEnabled: false
  });

  const onCredentialsError = useCallback((error: string | null) => {
    setState((prev) => ({
      ...prev,
      error: error ? new Error(error) : null
    }));
  }, []);

  const {
    connect: fetchCredentials,
    isConnecting: isFetchingCredentials,
    error: credentialsError
  } = useVoiceChatCredentials({
    config,
    onError: onCredentialsError
  });

  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new VoiceChatService();
    }

    const service = serviceRef.current;
    const unsubscribe = service.onStateChange((newState) => {
      setState(newState);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const isConnectingRef = useRef(false);
  const isConnectedRef = useRef(false);
  const lastConfigRef = useRef<string | null>(null);
  const manuallyDisconnectedRef = useRef(false);

  useEffect(() => {
    if (!config || !autoConnect) {
      return;
    }

    if (manuallyDisconnectedRef.current) {
      return;
    }

    const configKey = `${config.sessionId}-${config.participantId}-${config.deviceUuid}`;
    const configChanged = lastConfigRef.current !== configKey;

    if (configChanged) {
      isConnectingRef.current = false;
      isConnectedRef.current = false;
      manuallyDisconnectedRef.current = false;
      lastConfigRef.current = configKey;
    }

    if ((isConnectingRef.current || isConnectedRef.current) && !configChanged) {
      return;
    }

    let isMounted = true;
    isConnectingRef.current = true;

    const connect = async () => {
      try {
        setState((prev) => ({ ...prev, isConnecting: true, error: null }));

        const credentials = await fetchCredentials();

        if (credentials && serviceRef.current && isMounted) {
          await serviceRef.current.connect(credentials);
          if (isMounted) {
            isConnectedRef.current = true;
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('[useVoiceChat] Connection error:', error);
          setState((prev) => ({
            ...prev,
            error: error instanceof Error ? error : new Error(String(error)),
            isConnecting: false
          }));
        }
      } finally {
        if (isMounted) {
          isConnectingRef.current = false;
        }
      }
    };

    connect();

    return () => {
      isMounted = false;
      isConnectingRef.current = false;
      if (serviceRef.current) {
        serviceRef.current.disconnect().catch((error) => {
          console.error('[useVoiceChat] Provider disconnect error:', error);
        });
      }
      isConnectedRef.current = false;
    };
  }, [config?.sessionId, config?.participantId, config?.deviceUuid, autoConnect]);

  useEffect(() => {
    if (isFetchingCredentials && !isConnectingRef.current) {
      setState((prev) => ({ ...prev, isConnecting: true }));
    } else if (credentialsError && !isConnectingRef.current) {
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        error: credentialsError ? new Error(credentialsError) : null
      }));
    }
  }, [isFetchingCredentials, credentialsError]);

  useEffect(() => {
    if (state.isConnected) {
      isConnectedRef.current = true;
    } else if (!state.isConnecting) {
      isConnectedRef.current = false;
    }
  }, [state.isConnected, state.isConnecting]);

  const setMicrophoneEnabled = useCallback(async (enabled: boolean) => {
    if (serviceRef.current) {
      try {
        await serviceRef.current.setMicrophoneEnabled(enabled);
      } catch (error) {
        console.error('[useVoiceChat] Error setting microphone:', error);
        throw error;
      }
    }
  }, []);

  const connect = useCallback(async () => {
    if (!config || isConnectingRef.current || isConnectedRef.current) {
      return;
    }

    try {
      manuallyDisconnectedRef.current = false;
      isConnectingRef.current = true;
      setState((prev) => ({ ...prev, isConnecting: true, error: null }));

      const credentials = await fetchCredentials();

      if (credentials && serviceRef.current) {
        await serviceRef.current.connect(credentials);
        isConnectedRef.current = true;
      }
    } catch (error) {
      console.error('[useVoiceChat] Connect error:', error);
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error : new Error(String(error)),
        isConnecting: false
      }));
      throw error;
    } finally {
      isConnectingRef.current = false;
    }
  }, [config, fetchCredentials]);

  const disconnect = useCallback(async () => {
    manuallyDisconnectedRef.current = true;
    isConnectedRef.current = false;
    isConnectingRef.current = false;
    if (serviceRef.current) {
      await serviceRef.current.disconnect();
    }
    setState((prev) => ({
      ...prev,
      isConnected: false,
      isConnecting: false,
      participants: [],
      activeSpeakers: []
    }));
  }, []);

  const reconnect = useCallback(async () => {
    isConnectedRef.current = false;
    isConnectingRef.current = false;
    await disconnect();
    if (config && !isConnectingRef.current) {
      try {
        isConnectingRef.current = true;
        setState((prev) => ({ ...prev, isConnecting: true, error: null }));

        const credentials = await fetchCredentials();

        if (credentials && serviceRef.current) {
          await serviceRef.current.connect(credentials);
          isConnectedRef.current = true;
        }
      } catch (error) {
        console.error('[useVoiceChat] Reconnect error:', error);
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error : new Error(String(error)),
          isConnecting: false
        }));
      } finally {
        isConnectingRef.current = false;
      }
    }
  }, [config, fetchCredentials, disconnect]);

  return {
    ...state,
    setMicrophoneEnabled,
    connect,
    disconnect,
    reconnect
  };
}
